import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Search,
  ArrowLeft,
  X,
  Presentation,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Video,
  Link2,
  Unlink,
  ExternalLink,
  ListChecks,
  RefreshCw,
  CheckCircle2,
  Circle,
  Users,
} from 'lucide-react';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  deleteDoc,
  doc,
  updateDoc,
  deleteField,
} from 'firebase/firestore';
import BBALoader from '../../components/BBALoader';
import PortalBackground from '../../components/PortalBackground';
import CrmStatCard from '../../components/crm/CrmStatCard';
import MeetingSummaryContent from '../../components/MeetingSummaryContent';
import BreakthroughSessionBackground, {
  getBreakthroughTheme,
} from '../../components/portal/BreakthroughSessionBackground';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { getInternalUsers } from '../../services/userService';
import { getCrmContactDisplayPhotoUrl } from '../../services/firestoreService';
import { triggerNewTaskNotification } from '../../services/notificationTriggerService';
import { parseFathomSummarySections } from '../../utils/fathomSummaryParser';
import {
  toJsDate,
  actionItemText,
  fathomRecordingUrl,
  recordingStartDate,
  scoreRecordingForReport,
  findRecordingForReport,
  loadInternalRecordingsBetween,
  buildFathomLink,
  loadMeetingSummary,
  matchInternalUserByEmail,
  buildFathomTaskKeys,
  loadTasksForRecording,
  findTaskForActionItem,
  createInternalTaskFromActionItem,
} from '../../utils/meetingReportFathom';

/* ------------------------------------------------------------------ */
/* Shared bits                                                         */
/* ------------------------------------------------------------------ */

const DAY = 24 * 60 * 60 * 1000;
const CARD = 'rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm';
const PANEL = 'bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-200 dark:border-gray-700';
const PANEL_HEAD = 'bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 rounded-t-xl p-4';
const PANEL_TITLE = 'text-red-700 dark:text-red-300 font-semibold text-base text-center';
const TILE = 'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900';
const SOURCE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#F87171', '#8884d8'];
const CLIENT_PROGRAM_COLORS = {
  Lite: '#3B82F6',
  Plus: '#8B5CF6',
  Pro: '#10B981',
  Hourly: '#F59E0B',
  '5 Hours': '#EC4899',
  '10 Hours': '#06B6D4',
  'A La Carte': '#8B5CF6',
};
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const fmtLongDate = (d) =>
  d ? d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '';
const fmtDate = (d) => (d ? d.toLocaleDateString() : '');
const fmtShortDate = (d) => (d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const fmtTime = (d) => (d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '');

const departmentEmoji = (dept) => {
  const d = (dept || '').toLowerCase();
  if (d.includes('sales')) return '💰';
  if (d.includes('operation')) return '⚙️';
  if (d.includes('finance')) return '📊';
  if (d.includes('tech')) return '💻';
  if (d.includes('marketing')) return '📣';
  if (d.includes('hr') || d.includes('human')) return '👥';
  if (d.includes('onboard')) return '🚀';
  if (d.includes('design')) return '🎨';
  return '📋';
};

const initialsOf = (name) =>
  String(name || '')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

const Pill = ({ tone = 'gray', children, title }) => {
  const tones = {
    gray: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700',
    green: 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800',
    red: 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800',
    blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-800',
    amber: 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800',
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${tones[tone] || tones.gray}`}
    >
      {children}
    </span>
  );
};

const Avatar = ({ src, name, initials, size = 'w-10 h-10', ring = '' }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  if (src && !broken) {
    return (
      <img
        src={src}
        alt={name || ''}
        className={`${size} rounded-full object-cover flex-shrink-0 ${ring}`}
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <div
      className={`${size} rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-semibold text-gray-700 dark:text-gray-200 flex-shrink-0 ${ring}`}
    >
      {initials || initialsOf(name)}
    </div>
  );
};

const Logo = ({ src, name, size = 'w-12 h-12' }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  return (
    <div
      className={`${size} shrink-0 rounded-full overflow-hidden bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center`}
    >
      {src && !broken ? (
        <img src={src} alt={name || ''} className="max-w-[88%] max-h-[88%] object-contain" onError={() => setBroken(true)} />
      ) : (
        <span className="text-sm font-semibold text-gray-500 dark:text-gray-300">{initialsOf(name)}</span>
      )}
    </div>
  );
};

const Panel = ({ title, icon, children, className = '' }) => (
  <div className={`${PANEL} ${className}`}>
    <div className={PANEL_HEAD}>
      <h3 className={PANEL_TITLE}>
        {icon && (
          <span className="mr-2" aria-hidden="true">
            {icon}
          </span>
        )}
        {title}
      </h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const EmptyText = ({ children }) => (
  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">{children}</p>
);

const FireStatusPill = ({ status }) => {
  if (!status) return null;
  const tone =
    status === 'Active' ? 'red' : status === 'Pending' ? 'yellow' : status === 'Resolved' ? 'green' : 'gray';
  return <Pill tone={tone}>{status}</Pill>;
};

const isV2 = (report) => Number(report?.reportVersion) >= 2;

/* ------------------------------------------------------------------ */
/* Overview                                                             */
/* ------------------------------------------------------------------ */

function FiresPanel({ title, icon, fires, emptyText, legacy }) {
  return (
    <Panel title={`${title} (${fires.length})`} icon={icon}>
      {fires.length === 0 ? (
        <EmptyText>{emptyText}</EmptyText>
      ) : (
        <div className="space-y-3">
          {fires.slice(0, legacy ? 3 : 6).map((fire, idx) => {
            const deadline = toJsDate(fire.deadline);
            return (
              <div key={fire.id || idx} className="flex items-start gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0" />
                <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                  <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                    {fire.name}
                    {fire.companyName ? ` — ${fire.companyName}` : ''}
                    {deadline ? ` (Due: ${fmtDate(deadline)})` : ''}
                  </p>
                  <FireStatusPill status={fire.status} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

function ConferencesPanel({ conferences, reportDate }) {
  return (
    <Panel title={`Conferences (${conferences.length})`} icon="🎤">
      {conferences.length === 0 ? (
        <EmptyText>No upcoming conferences at the time of the meeting</EmptyText>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {conferences.slice(0, 6).map((c, idx) => {
            const start = toJsDate(c.startTime);
            const daysAway =
              Number.isFinite(c.daysAway) && c.daysAway !== null
                ? c.daysAway
                : start && reportDate
                ? Math.round((start - reportDate) / DAY)
                : null;
            const soon = daysAway !== null && daysAway <= 7;
            return (
              <div
                key={c.id || idx}
                className={`flex items-center gap-3 rounded-xl border p-3 ${
                  soon
                    ? 'border-amber-300 dark:border-amber-600 bg-amber-50/70 dark:bg-amber-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
                }`}
              >
                <div
                  className={`flex-shrink-0 w-16 rounded-lg py-2 text-center ${
                    soon ? 'bg-amber-100 dark:bg-amber-900/40' : 'bg-gray-100 dark:bg-gray-800'
                  }`}
                >
                  <div className="text-2xl font-bold leading-none text-gray-900 dark:text-white">
                    {daysAway === null ? '—' : daysAway <= 0 ? '🎉' : daysAway}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mt-1">
                    {daysAway === null ? 'no date' : daysAway <= 0 ? 'that day' : daysAway === 1 ? 'day away' : 'days away'}
                  </div>
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words leading-snug">
                    {c.title || c.name || 'Conference'}
                  </div>
                  {start && (
                    <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                      📅 {c.dateLabel || fmtShortDate(start)} {c.timeLabel ? `· ${c.timeLabel}` : ''}
                    </div>
                  )}
                  {c.location && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-words">📍 {c.location}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

function VisitGroupsPanel({ groups }) {
  const count = groups.reduce((n, g) => n + (g.companies || []).length, 0);
  return (
    <Panel title={`Client Visits (${count})`} icon="🚐">
      {groups.length === 0 ? (
        <EmptyText>No visits scheduled at the time of the meeting</EmptyText>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.key}>
              <div className="flex items-center justify-center gap-2 mb-3">
                {(g.types || []).map((t) => (
                  <span
                    key={t}
                    className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      t === 'CQC'
                        ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                        : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                    }`}
                  >
                    {t}
                  </span>
                ))}
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 text-center">{g.dateLabel}</span>
                {g.isWithin7Days && <span aria-hidden="true">⭐</span>}
              </div>
              <div className={`grid gap-4 ${(g.companies || []).length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {(g.companies || []).map((c, idx) => (
                  <div key={c.id || idx} className={`${TILE} flex flex-col items-center text-center p-3 gap-2`}>
                    <Logo src={c.logo} name={c.company} size="w-14 h-14" />
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words leading-tight w-full">
                      {c.company}
                    </span>
                    {(c.contactPhoto || c.contactName) && (
                      <div className="flex items-center gap-2 max-w-full">
                        <Avatar src={c.contactPhoto} name={c.contactName} size="w-8 h-8" />
                        {c.contactName && (
                          <span className="text-xs text-gray-600 dark:text-gray-300 break-words leading-tight text-left">
                            {c.contactName}
                          </span>
                        )}
                      </div>
                    )}
                    {c.status && (
                      <Pill tone={c.status === 'Met with' ? 'blue' : 'green'}>{c.status}</Pill>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function BreakthroughRow({ sessions }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 text-center mb-3">
        🎓 Next Breakthrough Sessions
      </h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }, (_, idx) => {
          const session = sessions[idx];
          if (!session) {
            return (
              <div key={`empty-${idx}`} className={`${PANEL} p-3 flex flex-col gap-2 opacity-50`}>
                <div className="flex items-center gap-2">
                  <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />
                  <div className="flex-1">
                    <div className="text-xs text-gray-400 dark:text-gray-500">No session</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">—</div>
                  </div>
                </div>
                <div className="text-sm text-gray-400 dark:text-gray-500">No upcoming session</div>
              </div>
            );
          }
          const theme = getBreakthroughTheme(session.department);
          const date = toJsDate(session.date);
          const initials = session.presenterInitials || initialsOf(session.presenter || session.label || 'BS');
          return (
            <div
              key={session.id || idx}
              className="relative overflow-hidden rounded-xl text-white shadow-sm"
              style={{ background: theme.gradient }}
            >
              <BreakthroughSessionBackground department={session.department} />
              <div className="relative z-10 p-3 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  {session.presenterPhoto ? (
                    <img
                      src={session.presenterPhoto}
                      alt={session.presenter || 'Presenter'}
                      className="h-10 w-10 rounded-full object-cover ring-2 ring-white/40"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-white/20 ring-2 ring-white/40 flex items-center justify-center text-white font-semibold text-xs">
                      {initials}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white/70">{session.label}</div>
                    <div className="text-xs text-white/85">{fmtShortDate(date)}</div>
                  </div>
                  <span className="inline-flex shrink-0 px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                    {session.department || 'Unassigned'}
                  </span>
                </div>
                {session.needPresenter ? (
                  <>
                    <div className="text-sm font-semibold text-white flex items-center gap-1">
                      <span>⚠️</span>
                      <span>Need Presenter</span>
                    </div>
                    <div className="text-xs text-white/85">
                      {fmtShortDate(date)} • {session.startTime || ''} {session.startTime && session.endTime ? '-' : ''}{' '}
                      {session.endTime || ''}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm font-semibold text-white">{session.eventName}</div>
                    <div className="text-xs text-white/85">
                      {session.presenter ? `Presenter: ${session.presenter}` : 'No presenter yet'}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OverviewSection({ report, reportDate }) {
  const v2 = isV2(report);
  const internalFires = report.internalFires || [];
  const clientFires = report.clientFires || [];
  const conferences = report.conferences || [];

  // Legacy reports stored a flat visit list; group it by date so both shapes render alike.
  const visitGroups = useMemo(() => {
    if (Array.isArray(report.clientVisitGroups)) return report.clientVisitGroups;
    const byDate = new Map();
    (report.clientVisits || []).forEach((v, idx) => {
      const d = toJsDate(v.visitDate || v.preferredDate);
      const key = d ? d.toDateString() : 'unknown';
      if (!byDate.has(key)) {
        byDate.set(key, { key, dateLabel: d ? fmtShortDate(d) : 'No date', types: [], companies: [] });
      }
      const g = byDate.get(key);
      if (v.visitType && !g.types.includes(v.visitType)) g.types.push(v.visitType);
      g.companies.push({
        id: v.id || idx,
        company: v.companyName || v.text || 'Visit',
        contactName: v.contactName || '',
        status: v.status === 'completed' ? 'Met with' : v.status === 'scheduled' ? 'Scheduled' : v.status || '',
      });
    });
    return Array.from(byDate.values());
  }, [report]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FiresPanel title="Internal Fires" icon="🔥" fires={internalFires} emptyText="No internal fires reported" legacy={!v2} />
        <FiresPanel title="Client Fires" icon="🚨" fires={clientFires} emptyText="No client fires reported" legacy={!v2} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ConferencesPanel conferences={conferences} reportDate={reportDate} />
        <VisitGroupsPanel groups={visitGroups} />
      </div>
      {v2 ? (
        <BreakthroughRow sessions={report.breakthroughSessions || []} />
      ) : (
        report.departmentCounts && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ['General', '⚙️', report.departmentCounts.general],
              ['Tech', '💻', report.departmentCounts.technology],
              ['Marketing', '📣', report.departmentCounts.marketing],
              ['HR', '👥', report.departmentCounts.hr],
            ].map(([name, emoji, count]) => (
              <div key={name} className={`${TILE} p-4 text-center`}>
                <div className="text-2xl" aria-hidden="true">
                  {emoji}
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{count || 0}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{name} fulfillment requests</div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Growth — Last 30 Days                                                */
/* ------------------------------------------------------------------ */

function SourcePie({ heading, emoji, slices }) {
  const colored = (slices || []).map((s, i) => ({ ...s, color: SOURCE_COLORS[i % SOURCE_COLORS.length] }));
  const total = colored.reduce((sum, s) => sum + (s.value || 0), 0);
  let cursor = 0;
  const gradient = colored.map((s) => {
    const pct = total ? (s.value / total) * 100 : 0;
    const part = `${s.color} ${cursor}% ${cursor + pct}%`;
    cursor += pct;
    return part;
  });
  return (
    <Panel title={heading} icon={emoji}>
      {total === 0 ? (
        <EmptyText>No lead source data in that 30-day window</EmptyText>
      ) : (
        <div className="flex items-center justify-center gap-8 flex-wrap py-2">
          <div
            className="w-40 h-40 rounded-full shadow-inner flex-shrink-0"
            role="img"
            aria-label={`${heading} pie chart`}
            style={{ background: `conic-gradient(${gradient.join(', ')})` }}
          />
          <div className="space-y-2 text-sm text-gray-700 dark:text-gray-200">
            {colored.map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: s.color }} />
                <span className="min-w-[140px]">{s.name}</span>
                <span className="font-medium">{s.value}</span>
                <span className="text-gray-500 dark:text-gray-400">({((s.value / total) * 100).toFixed(0)}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}

function GrowthSection({ growth }) {
  if (!growth) {
    return (
      <div className={`${PANEL} p-8 text-center text-sm text-gray-500 dark:text-gray-400`}>
        This report was saved by the previous Meeting Mode, before the Growth slide existed.
      </div>
    );
  }
  const website = growth.website || {};
  const avgSeconds = website.avgSeconds || 0;
  const stat = (emoji, label, value, cls) => (
    <div className={`${TILE} p-4 text-center`}>
      <div className="text-3xl leading-none" aria-hidden="true">
        {emoji}
      </div>
      <div className={`mt-2 text-4xl font-bold ${cls}`}>{value}</div>
      <div className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">{label}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">30 days before the meeting</div>
    </div>
  );
  const borderColors = {
    Facebook: 'border-blue-500',
    Instagram: 'border-pink-500',
    TikTok: 'border-black dark:border-gray-300',
    YouTube: 'border-red-500',
    LinkedIn: 'border-blue-600',
  };
  const textColors = {
    Facebook: 'text-blue-600 dark:text-blue-300',
    Instagram: 'text-pink-600 dark:text-pink-300',
    TikTok: 'text-gray-700 dark:text-gray-300',
    YouTube: 'text-red-600 dark:text-red-300',
    LinkedIn: 'text-blue-600 dark:text-blue-300',
  };
  const platformIcons = {
    Facebook: '/facebook.svg',
    Instagram: '/instagram.svg',
    TikTok: '/tiktok.svg',
    YouTube: '/Youtube.svg',
    LinkedIn: '/Linkedin.svg',
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {stat('🌱', 'New leads', growth.newLeads || 0, 'text-green-600 dark:text-green-400')}
        {stat('🤝', 'New clients', growth.newClients || 0, 'text-blue-600 dark:text-blue-400')}
        {stat('👋', 'Lost clients', growth.lostClients || 0, 'text-red-600 dark:text-red-400')}
      </div>
      <p className="-mt-6 text-xs text-center text-gray-500 dark:text-gray-400">
        New clients are counted from their start date; lost clients from when the account was last updated.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SourcePie heading="Lead Source — New Leads" emoji="🌱" slices={growth.leadSourceSlices} />
        <SourcePie heading="Lead Source — New Clients" emoji="🤝" slices={growth.clientSourceSlices} />
      </div>
      <Panel title="Website & Organic Traffic" icon="🌐">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            ['👥 Total Visitors', (website.totalVisitors || 0).toLocaleString()],
            ['⏱️ Avg. Session Duration', avgSeconds < 60 ? `${Math.round(avgSeconds)}s` : `${(avgSeconds / 60).toFixed(1)}m`],
            ['↩️ Bounce Rate', `${(website.avgBounce || 0).toFixed(1)}%`],
          ].map(([label, value]) => (
            <div key={label} className={`${TILE} p-4 text-center`}>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</div>
              <div className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">{value}</div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">30 days before the meeting</div>
            </div>
          ))}
        </div>
      </Panel>
      <Panel title="Social Media Performance" icon="📱">
        {growth.hasSocialData && (growth.social || []).length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {growth.social.map(({ platform, followers, change }) => (
              <div
                key={platform}
                className={`${TILE} p-4 shadow-md border-l-4 ${borderColors[platform] || 'border-gray-300'} relative`}
              >
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 mb-3 flex items-center justify-center">
                    <img
                      src={platformIcons[platform] || `/${String(platform).toLowerCase()}.svg`}
                      alt={`${platform} icon`}
                      className="w-14 h-14"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <h3 className="text-sm font-bold text-center text-gray-800 dark:text-white">{platform}</h3>
                  <div className={`text-lg font-bold mt-2 ${textColors[platform] || ''}`}>{(followers || 0).toLocaleString()}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">followers</div>
                  {change && (
                    <div
                      className={`flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-full border-2 font-semibold text-sm ${
                        change.isIncrease
                          ? 'text-green-700 dark:text-green-300 border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/30'
                          : 'text-red-700 dark:text-red-300 border-red-600 dark:border-red-400 bg-red-50 dark:bg-red-900/30'
                      }`}
                    >
                      <span aria-hidden="true">{change.isIncrease ? '⬆️' : '⬇️'}</span>
                      <span>{(change.value || 0).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyText>No social media data available</EmptyText>
        )}
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Clients                                                              */
/* ------------------------------------------------------------------ */

function ClientCard({ row, attention, monthLabel }) {
  const resetDate = toJsDate(row.resetDate);
  const lastEmail = toJsDate(row.lastEmailDate);
  const checkIn = row.checkIn;
  const renderCall = (c, i, upcoming) => {
    const d = toJsDate(c.date);
    return (
      <div key={i} className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <Avatar src={c.photo} name={c.label} initials={c.initials} size="w-9 h-9" />
          <span className="text-base text-gray-800 dark:text-gray-100 truncate">{c.label}</span>
        </div>
        <span
          className={`text-sm font-medium whitespace-nowrap ${
            upcoming ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          {fmtDate(d)}
        </span>
      </div>
    );
  };

  return (
    <div
      className={`rounded-xl border p-4 ${
        attention
          ? 'border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/10'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Logo src={row.logo} name={row.company} />
          <div className="min-w-0">
            <div className="text-lg font-semibold text-gray-800 dark:text-gray-100 truncate">{row.company || 'Unknown'}</div>
            {row.stage && (
              <span
                className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full"
                style={{
                  border: `1.5px solid ${CLIENT_PROGRAM_COLORS[row.stage] || '#6B7280'}`,
                  color: CLIENT_PROGRAM_COLORS[row.stage] || '#6B7280',
                }}
              >
                {row.stage}
              </span>
            )}
          </div>
        </div>
        {row.limit > 0 && (
          <div className="flex-shrink-0 text-center rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-2">
            <div className="text-4xl font-bold leading-none text-gray-900 dark:text-white">
              {row.used}
              <span className="text-gray-400 dark:text-gray-500">/</span>
              {row.limit}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mt-1">1:1 calls</div>
            {resetDate && (
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 whitespace-nowrap">
                🔄 resets {fmtShortDate(resetDate)}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <Pill>
          ⏳{' '}
          {row.monthsWithUs == null
            ? 'Start date unknown'
            : row.monthsWithUs === 0
            ? 'First month with us'
            : `${row.monthsWithUs} ${row.monthsWithUs === 1 ? 'month' : 'months'} with us`}
        </Pill>
        <Pill tone={!checkIn ? 'gray' : checkIn.responded ? 'green' : 'yellow'} title={checkIn?.comments || ''}>
          {!checkIn
            ? `📝 No ${monthLabel} check-in`
            : checkIn.responded
            ? `⭐ ${monthLabel} check-in: ${checkIn.rating != null ? `${checkIn.rating}/5` : checkIn.status}`
            : `⏰ ${monthLabel} check-in sent, no reply`}
        </Pill>
        {lastEmail && <Pill>✉️ Last email {fmtDate(lastEmail)}</Pill>}
      </div>

      {(row.members || []).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-4">
          {row.members.map((m, i) => (
            <div key={m.id || i} className="flex flex-col items-center w-20 text-center">
              <div className="relative">
                <Avatar
                  src={m.photo}
                  name={m.name}
                  initials={m.initials}
                  size="w-14 h-14"
                  ring={m.pinned ? 'ring-2 ring-red-500' : 'ring-1 ring-gray-200 dark:ring-gray-600'}
                />
                {m.pinned && (
                  <span className="absolute -top-1 -right-1 text-xs leading-none" aria-hidden="true">
                    📌
                  </span>
                )}
              </div>
              <span className="mt-1 text-xs text-gray-700 dark:text-gray-200 w-full break-words leading-tight" title={m.name}>
                {m.name}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
          <span aria-hidden="true">📞</span> Met with · 30 days before the meeting
        </div>
        {(row.recentCalls || []).length === 0 ? (
          <div className="text-sm text-amber-700 dark:text-amber-400">No calls logged</div>
        ) : (
          <div className="space-y-2">{row.recentCalls.slice(0, 4).map((c, i) => renderCall(c, i, false))}</div>
        )}
      </div>

      {(row.upcomingCalls || []).length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-blue-700 dark:text-blue-300 mb-2">
            <span aria-hidden="true">📅</span> Upcoming booked
          </div>
          <div className="space-y-2">{row.upcomingCalls.slice(0, 4).map((c, i) => renderCall(c, i, true))}</div>
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
          <span aria-hidden="true">🗺️</span> Next on roadmap
        </div>
        {(row.nextSteps || []).length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">No upcoming roadmap items</div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {row.nextSteps.map((s, i) => (
              <div
                key={i}
                className="flex flex-col items-center text-center rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-2"
              >
                <span className="text-2xl leading-none">{departmentEmoji(s.department)}</span>
                <span className="mt-1 text-xs font-medium text-gray-800 dark:text-gray-100 line-clamp-2">{s.title}</span>
                <span className="mt-0.5 text-[10px] text-gray-400 dark:text-gray-500">{s.department}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ClientsSection({ report }) {
  if (!isV2(report)) {
    const accounts = (report.allAccounts || []).filter((a) => a && (a.type === 'Clients' || a.type === 'clients'));
    if (accounts.length === 0) return <EmptyText>No clients were captured in this report</EmptyText>;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {accounts.map((account) => (
          <div key={account.id} className={`${TILE} p-5 flex items-center gap-4`}>
            <Avatar
              src={getCrmContactDisplayPhotoUrl(account)}
              name={`${account.contact?.firstName || ''} ${account.contact?.lastName || ''}`}
              size="w-14 h-14"
            />
            <div className="flex-1 min-w-0">
              <div className="text-lg font-semibold truncate text-gray-800 dark:text-gray-100">{account.company}</div>
              {account.stage && <Pill>{account.stage}</Pill>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const activity = report.clientActivity || { rows: [], totals: {} };
  const totals = activity.totals || {};
  const monthLabel = activity.monthLabel || 'this month';
  const rows = activity.rows || [];
  const groups = [
    { key: 'active', title: 'Recently Active', subtitle: 'Met in the 30 days before the meeting', match: (r) => (r.recentCalls || []).length > 0 },
    { key: 'attention', title: 'Needs Attention', subtitle: 'No 1:1 call in the 30 days before the meeting', match: (r) => (r.recentCalls || []).length === 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          ['📅 Allotted', totals.total || 0, 'text-gray-900 dark:text-white', 'across all live cycles'],
          [`✅ Used in ${monthLabel}`, totals.used || 0, 'text-blue-600 dark:text-blue-400', 'calls logged that month'],
          ['🎟️ Still bookable', totals.remaining || 0, 'text-green-600 dark:text-green-400', 'left before each reset'],
        ].map(([label, value, cls, sub]) => (
          <div key={label} className={`${TILE} p-4 text-center`}>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</div>
            <div className={`mt-1 text-3xl font-bold ${cls}`}>{value}</div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{sub}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-center text-gray-500 dark:text-gray-400">
        Every client resets on their own date, so allotted ≠ used + bookable. Each card shows its own reset day.
      </p>
      {rows.length === 0 ? (
        <EmptyText>No active clients were captured in this report</EmptyText>
      ) : (
        groups.map((group) => {
          const groupRows = rows.filter(group.match);
          if (groupRows.length === 0) return null;
          return (
            <div key={group.key} className="space-y-3">
              <div className="flex items-center justify-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">{group.title}</h3>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  {groupRows.length}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">{group.subtitle}</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {groupRows.map((row) => (
                  <ClientCard key={row.id} row={row} attention={group.key === 'attention'} monthLabel={monthLabel} />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Outstanding Tasks                                                    */
/* ------------------------------------------------------------------ */

function TasksSection({ report, reportDate }) {
  const users = report.internalUsers || [];
  const tasksByUser = report.tasksByUser || {};
  const dayStart = reportDate ? new Date(reportDate) : null;
  if (dayStart) dayStart.setHours(0, 0, 0, 0);

  if (users.length === 0) return <EmptyText>No team members were captured in this report</EmptyText>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {users.map((user) => {
        const tasks = [...(tasksByUser[user.id] || [])].sort((a, b) => {
          const da = toJsDate(a.dueDate);
          const dbb = toJsDate(b.dueDate);
          if (!da && !dbb) return 0;
          if (!da) return 1;
          if (!dbb) return -1;
          return da - dbb;
        });
        return (
          <div key={user.id} className={`${PANEL} flex flex-col`}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col items-center">
              <Avatar src={user.photoURL} name={user.name} initials={user.initials} size="w-12 h-12" />
              <div className="mt-2 text-sm font-medium text-gray-800 dark:text-gray-100 text-center truncate max-w-[160px]">
                {user.name || 'User'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {tasks.length} open {tasks.length === 1 ? 'task' : 'tasks'}
              </div>
            </div>
            <div className="p-3 space-y-2 overflow-y-auto" style={{ maxHeight: '360px' }}>
              {tasks.length === 0 ? (
                <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-6">No tasks</div>
              ) : (
                tasks.map((task, idx) => {
                  const due = toJsDate(task.dueDate);
                  const overdue = !!(due && dayStart && due < dayStart);
                  return (
                    <div
                      key={task.id || idx}
                      className={`rounded-lg p-3 border ${
                        overdue
                          ? 'border-red-300 dark:border-red-700 bg-red-50/70 dark:bg-red-900/10'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
                      }`}
                    >
                      <div
                        className={`text-sm font-semibold ${
                          overdue ? 'text-red-700 dark:text-red-300' : 'text-gray-800 dark:text-gray-100'
                        }`}
                      >
                        {task.title || 'Untitled task'}
                      </div>
                      <div className={`mt-1 text-xs ${overdue ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        {due ? `${overdue ? 'Was overdue · ' : ''}${fmtDate(due)}` : 'No date'}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Additional Items                                                     */
/* ------------------------------------------------------------------ */

function AdditionalSection({ report }) {
  const list = (items, empty) =>
    items.length === 0 ? (
      <EmptyText>{empty}</EmptyText>
    ) : (
      <div className="space-y-3">
        {items.map((t, idx) => (
          <div key={idx} className={`${TILE} p-4`}>
            <p className="text-sm text-gray-700 dark:text-gray-200 break-words">{t}</p>
          </div>
        ))}
      </div>
    );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Panel title="Questions" icon="❓">
        {list(report.questions || [], 'No questions were raised')}
      </Panel>
      <Panel title="Comments" icon="💬">
        {list(report.comments || [], 'No comments were added')}
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Recording & Action Items (Fathom)                                    */
/* ------------------------------------------------------------------ */

function RecordingSection({
  report,
  reportDate,
  summary,
  summaryLoading,
  autoLinking,
  internalUsers,
  recordingTasks,
  itemKeys,
  assigneeById,
  onPickAssignee,
  creating,
  onCreateTask,
  onCreateAll,
  onOpenPicker,
  onUnlink,
  onRetryAutoLink,
}) {
  const link = report.fathom;
  const recorder = link?.recorderUid ? internalUsers.find((u) => u.id === link.recorderUid) : null;
  const url = link?.recordingUrl || fathomRecordingUrl(summary);
  const start = toJsDate(link?.scheduledStartTime) || recordingStartDate(summary);
  const actionItems = (summary?.actionItems || []).filter((item) => actionItemText(item));
  const sections = useMemo(() => parseFathomSummarySections(summary?.summary || ''), [summary]);

  const rows = actionItems.map((item, idx) => {
    const keys = itemKeys[idx] || null;
    const task = findTaskForActionItem(item, keys, recordingTasks);
    const fathomEmail = typeof item === 'object' ? item.assigneeEmail || '' : '';
    const matched = matchInternalUserByEmail(fathomEmail, internalUsers);
    const assigneeId = assigneeById[idx] !== undefined ? assigneeById[idx] : matched?.id || '';
    return { item, idx, keys, task, fathomEmail, matched, assigneeId };
  });
  const pending = rows.filter((r) => !r.task && r.assigneeId);
  const done = rows.filter((r) => r.task).length;

  if (!link) {
    return (
      <div className={`${PANEL} p-8 text-center space-y-4`}>
        <img src="/fathomlogo.png" alt="Fathom" className="w-14 h-14 rounded-full object-contain mx-auto" />
        <div className="text-lg font-semibold text-gray-900 dark:text-white">No Fathom recording linked yet</div>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
          {autoLinking
            ? 'Looking for an internal recording around the time this report was saved…'
            : 'Reports link automatically once Fathom finishes processing the recording (usually a few minutes after the call). You can also pick the recording yourself.'}
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={onRetryAutoLink}
            disabled={autoLinking}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${autoLinking ? 'animate-spin' : ''}`} />
            Check again
          </button>
          <button
            type="button"
            onClick={onOpenPicker}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium"
          >
            <Link2 className="w-4 h-4" />
            Pick a recording
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Recording header */}
      <div className={`${TILE} p-5`}>
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <img src="/fathomlogo.png" alt="Fathom" className="w-14 h-14 rounded-full object-contain flex-shrink-0 mx-auto md:mx-0" />
          <div className="flex-1 min-w-0 text-center md:text-left">
            <div className="text-lg font-semibold text-gray-900 dark:text-white break-words">
              {link.meetingTitle || summary?.meetingTitle || 'Fathom recording'}
            </div>
            <div className="mt-1 flex flex-wrap items-center justify-center md:justify-start gap-2 text-xs">
              {start && <Pill>📅 {fmtLongDate(start)} · {fmtTime(start)}</Pill>}
              {recorder && <Pill>🎙️ Recorded by {recorder.name}</Pill>}
              <Pill tone={link.linkedBy === 'auto' ? 'green' : 'blue'}>
                {link.linkedBy === 'auto' ? '✨ Linked automatically' : '🔗 Linked manually'}
              </Pill>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium"
              >
                <Video className="w-4 h-4" />
                Open recording
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            <button
              type="button"
              onClick={onOpenPicker}
              title="Link a different recording"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Link2 className="w-4 h-4" />
              Change
            </button>
            <button
              type="button"
              onClick={onUnlink}
              title="Unlink this recording"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Unlink className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {summaryLoading ? (
        <div className="flex justify-center items-center py-8">
          <BBALoader size={32} />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading the Fathom summary…</span>
        </div>
      ) : !summary ? (
        <div className={`${PANEL} p-6 text-center text-sm text-gray-500 dark:text-gray-400`}>
          The linked Fathom summary could not be found anymore. Unlink it and pick the recording again.
        </div>
      ) : (
        <>
          {/* Action items */}
          <Panel
            title={`Action Items (${done}/${actionItems.length} assigned)`}
            icon="✅"
          >
            {actionItems.length === 0 ? (
              <EmptyText>Fathom found no action items in this recording</EmptyText>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Each action item becomes a CRM task for the chosen team member. Fathom's assignee is pre-selected
                    when their email matches an internal user.
                  </p>
                  <div className="flex items-center gap-2">
                    <a
                      href="/crm/tasks"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <ListChecks className="w-3.5 h-3.5" />
                      Open Tasks
                    </a>
                    <button
                      type="button"
                      onClick={onCreateAll}
                      disabled={pending.length === 0 || Object.values(creating).some(Boolean)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Users className="w-3.5 h-3.5" />
                      Assign all ({pending.length})
                    </button>
                  </div>
                </div>
                <ul className="space-y-2">
                  {rows.map(({ item, idx, task, fathomEmail, matched, assigneeId }) => {
                    const text = actionItemText(item);
                    const assigneeName = typeof item === 'object' ? item.assigneeName : '';
                    const taskAssignee = task
                      ? internalUsers.find((u) => (task.assignees || []).includes(u.id))
                      : null;
                    const isCreating = !!creating[idx];
                    return (
                      <li key={idx} className={`${TILE} p-3 flex flex-col md:flex-row md:items-center gap-3`}>
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          {task ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <Circle className="w-5 h-5 text-gray-300 dark:text-gray-600 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm text-gray-900 dark:text-white leading-relaxed">{text}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {assigneeName || fathomEmail
                                ? `Fathom assigned: ${assigneeName || ''}${fathomEmail ? ` (${fathomEmail})` : ''}`
                                : 'Fathom did not name an assignee'}
                              {typeof item === 'object' && item.completed ? ' · marked done in Fathom' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 md:justify-end flex-shrink-0">
                          {task ? (
                            <Pill tone="green" title={`Task ${task.id}`}>
                              ✅ Task created{taskAssignee ? ` · ${taskAssignee.name}` : ''}
                              {task.completed ? ' · completed' : ''}
                            </Pill>
                          ) : (
                            <>
                              <select
                                value={assigneeId}
                                onChange={(e) => onPickAssignee(idx, e.target.value)}
                                className="text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-1.5 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              >
                                <option value="">Assign to…</option>
                                {internalUsers.map((u) => (
                                  <option key={u.id} value={u.id}>
                                    {u.name || u.email || u.id}
                                    {matched && matched.id === u.id ? ' (Fathom match)' : ''}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => onCreateTask(idx)}
                                disabled={!assigneeId || isCreating}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                              >
                                {isCreating ? 'Creating…' : 'Create task'}
                              </button>
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </Panel>

          {/* Summary */}
          {summary.summary ? (
            sections.hasStructuredSections ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  ['Meeting Purpose', '🎯', sections.meetingPurpose],
                  ['Key Takeaways', '💡', sections.keyTakeaways],
                  ['Topics', '🗂️', sections.topics],
                  ['Next Steps', '➡️', sections.nextSteps],
                ]
                  .filter(([, , body]) => body && body.trim())
                  .map(([title, emoji, body]) => (
                    <Panel key={title} title={title} icon={emoji}>
                      <MeetingSummaryContent markdown={body} className="!bg-transparent !p-0" />
                    </Panel>
                  ))}
              </div>
            ) : (
              <Panel title="Fathom Summary" icon="📝">
                <MeetingSummaryContent markdown={summary.summary} className="!bg-transparent !p-0" />
              </Panel>
            )
          ) : (
            <div className={`${PANEL} p-6 text-center text-sm text-gray-500 dark:text-gray-400`}>
              Fathom did not include a written summary for this recording.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RecordingPicker({ open, onClose, reportDate, internalUsers, onLink, excludeSummaryId }) {
  const [wide, setWide] = useState(false);
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !reportDate) return undefined;
    let active = true;
    const span = wide ? 21 * DAY : 3 * DAY;
    setLoading(true);
    setError('');
    loadInternalRecordingsBetween(db, new Date(reportDate.getTime() - span), new Date(reportDate.getTime() + span))
      .then((list) => {
        if (!active) return;
        setCandidates(list.filter((s) => s.id !== excludeSummaryId));
      })
      .catch((e) => {
        console.error('Failed to load Fathom recordings:', e);
        if (active) setError('Could not load recordings. Please try again.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, wide, reportDate, excludeSummaryId]);

  if (!open) return null;

  const suggested = candidates
    .map((c) => ({ c, score: scoreRecordingForReport(c, reportDate) }))
    .filter((x) => x.score != null)
    .sort((a, b) => a.score - b.score)[0]?.c;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className={`${CARD} w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-center relative">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white text-center">Link a Fathom recording</h2>
          <button type="button" onClick={onClose} className="absolute right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap text-sm">
          <span className="text-gray-600 dark:text-gray-300">
            Internal recordings within {wide ? '3 weeks' : '3 days'} of {fmtLongDate(reportDate)}
          </span>
          <label className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-300 cursor-pointer">
            <input type="checkbox" checked={wide} onChange={(e) => setWide(e.target.checked)} className="rounded" />
            Search a wider window
          </label>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <BBALoader size={32} />
            </div>
          ) : error ? (
            <EmptyText>{error}</EmptyText>
          ) : candidates.length === 0 ? (
            <EmptyText>No internal Fathom recordings in that window.</EmptyText>
          ) : (
            candidates.map((c) => {
              const start = recordingStartDate(c);
              const recorder = internalUsers.find((u) => u.id === c.recorderUid);
              const items = (c.actionItems || []).length;
              const isSuggested = suggested && suggested.id === c.id;
              return (
                <div
                  key={c.id}
                  className={`${TILE} p-3 flex items-center gap-3 ${
                    isSuggested ? 'border-green-300 dark:border-green-700 bg-green-50/40 dark:bg-green-900/10' : ''
                  }`}
                >
                  <img src="/fathomlogo.png" alt="" className="w-9 h-9 rounded-full object-contain flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {c.meetingTitle || 'Untitled recording'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-3">
                      {start && (
                        <span>
                          📅 {fmtDate(start)} {fmtTime(start)}
                        </span>
                      )}
                      {recorder && <span>🎙️ {recorder.name}</span>}
                      <span>✅ {items} action {items === 1 ? 'item' : 'items'}</span>
                      {isSuggested && <span className="text-green-700 dark:text-green-300 font-medium">Suggested</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onLink(c)}
                    className="px-3 py-1.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-medium"
                  >
                    Link
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

const MEETINGS_PER_PAGE = 12;
const AUTO_LINK_LOOKBACK_MS = 14 * DAY;
const AUTO_LINK_MAX = 5;

const MeetingsReports = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [internalUsers, setInternalUsers] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);

  // Fathom state for the open report
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [autoLinking, setAutoLinking] = useState(false);
  const [recordingTasks, setRecordingTasks] = useState([]);
  const [itemKeys, setItemKeys] = useState([]);
  const [assigneeById, setAssigneeById] = useState({});
  const [creating, setCreating] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const autoLinkTried = useRef(new Set());

  const selectedReport = useMemo(() => reports.find((r) => r.id === selectedId) || null, [reports, selectedId]);
  const selectedDate = selectedReport ? toJsDate(selectedReport.createdAt) || toJsDate(selectedReport.createdAtISO) : null;
  const linkedSummaryId = selectedReport?.fathom?.summaryId || null;

  /* ---------- data ---------- */

  useEffect(() => {
    const q = query(collection(db, 'meetingReports'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setReports(
          snapshot.docs.map((d) => {
            const data = d.data();
            const date = toJsDate(data.createdAt) || toJsDate(data.createdAtISO);
            return { id: d.id, ...data, _date: date };
          })
        );
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching meetings:', error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    getInternalUsers()
      .then((users) => setInternalUsers(users || []))
      .catch((e) => {
        console.error('Error loading internal users:', e);
        setInternalUsers([]);
      });
  }, []);

  const userById = useMemo(() => Object.fromEntries(internalUsers.map((u) => [u.id, u])), [internalUsers]);

  const persistLink = useCallback(
    async (reportId, recording, linkedBy) => {
      await updateDoc(doc(db, 'meetingReports', reportId), {
        fathom: buildFathomLink(recording, { linkedBy }),
      });
    },
    []
  );

  const tryAutoLink = useCallback(
    async (report) => {
      const date = report?._date || toJsDate(report?.createdAt);
      if (!report?.id || !date) return null;
      autoLinkTried.current.add(report.id);
      try {
        const recording = await findRecordingForReport(db, date);
        if (recording) await persistLink(report.id, recording, 'auto');
        return recording;
      } catch (e) {
        console.error('Fathom auto-link failed for report', report.id, e);
        return null;
      }
    },
    [persistLink]
  );

  // Background sweep: link recent reports that don't have a recording yet. Bounded so a
  // page load never fans out into more than a handful of Firestore reads.
  useEffect(() => {
    if (loading || reports.length === 0) return undefined;
    let active = true;
    const cutoff = Date.now() - AUTO_LINK_LOOKBACK_MS;
    const targets = reports
      .filter((r) => !r.fathom && r._date && r._date.getTime() >= cutoff && !autoLinkTried.current.has(r.id))
      .slice(0, AUTO_LINK_MAX);
    if (targets.length === 0) return undefined;
    (async () => {
      for (const report of targets) {
        if (!active) return;
        await tryAutoLink(report);
      }
    })();
    return () => {
      active = false;
    };
  }, [loading, reports, tryAutoLink]);

  // The open report: load its linked summary + tasks, or try to link it once.
  useEffect(() => {
    if (!selectedReport) {
      setSummary(null);
      setRecordingTasks([]);
      setItemKeys([]);
      return undefined;
    }
    let active = true;
    setAssigneeById({});
    setCreating({});

    if (!linkedSummaryId) {
      setSummary(null);
      setRecordingTasks([]);
      setItemKeys([]);
      if (!autoLinkTried.current.has(selectedReport.id)) {
        setAutoLinking(true);
        tryAutoLink(selectedReport).finally(() => {
          if (active) setAutoLinking(false);
        });
      }
      return () => {
        active = false;
      };
    }

    setSummaryLoading(true);
    (async () => {
      try {
        const loaded = await loadMeetingSummary(db, linkedSummaryId);
        if (!active) return;
        setSummary(loaded);
        if (loaded) {
          const items = (loaded.actionItems || []).filter((item) => actionItemText(item));
          const [tasks, keys] = await Promise.all([
            loadTasksForRecording(db, loaded.recordingId),
            Promise.all(items.map((item) => buildFathomTaskKeys({ summary: loaded, item }))),
          ]);
          if (!active) return;
          setRecordingTasks(tasks);
          setItemKeys(keys);
        } else {
          setRecordingTasks([]);
          setItemKeys([]);
        }
      } catch (e) {
        console.error('Failed to load the linked Fathom summary:', e);
        if (active) {
          setSummary(null);
          setRecordingTasks([]);
          setItemKeys([]);
        }
      } finally {
        if (active) setSummaryLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [selectedReport, linkedSummaryId, tryAutoLink]);

  useEffect(() => {
    if (selectedId) setCurrentSection(0);
  }, [selectedId]);

  /* ---------- handlers ---------- */

  const handleRetryAutoLink = async () => {
    if (!selectedReport) return;
    setAutoLinking(true);
    try {
      const recording = await tryAutoLink(selectedReport);
      if (!recording) {
        alert('No internal Fathom recording was found near the time of this meeting yet. If Fathom is still processing, check again in a few minutes — or pick the recording manually.');
      }
    } finally {
      setAutoLinking(false);
    }
  };

  const handleManualLink = async (recording) => {
    if (!selectedReport) return;
    try {
      await persistLink(selectedReport.id, recording, currentUser?.uid || 'manual');
      setPickerOpen(false);
    } catch (e) {
      console.error('Failed to link recording:', e);
      alert('Could not link that recording. Please try again.');
    }
  };

  const handleUnlink = async () => {
    if (!selectedReport?.fathom) return;
    if (!window.confirm('Unlink this Fathom recording from the report? Tasks already created stay in place.')) return;
    try {
      await updateDoc(doc(db, 'meetingReports', selectedReport.id), { fathom: deleteField() });
      autoLinkTried.current.add(selectedReport.id); // don't immediately re-link the same recording
      setSummary(null);
    } catch (e) {
      console.error('Failed to unlink recording:', e);
      alert('Could not unlink the recording. Please try again.');
    }
  };

  const createTaskAt = async (idx) => {
    if (!summary || !selectedReport) return false;
    const items = (summary.actionItems || []).filter((item) => actionItemText(item));
    const item = items[idx];
    if (!item) return false;
    const fathomEmail = typeof item === 'object' ? item.assigneeEmail || '' : '';
    const assigneeId =
      assigneeById[idx] !== undefined ? assigneeById[idx] : matchInternalUserByEmail(fathomEmail, internalUsers)?.id || '';
    const assignee = userById[assigneeId];
    if (!assignee) return false;

    setCreating((prev) => ({ ...prev, [idx]: true }));
    try {
      const result = await createInternalTaskFromActionItem(db, {
        summary,
        item,
        assignee,
        createdBy: currentUser?.uid || null,
        meetingReportId: selectedReport.id,
      });
      if (!result.existed) {
        try {
          await triggerNewTaskNotification({
            taskTitle: result.payload.title,
            description: result.payload.description,
            assignees: result.payload.assignees,
            dueDate: null,
            priority: result.payload.priority,
            createdBy: currentUser?.uid || null,
            accountId: null,
          });
        } catch (e) {
          console.warn('Task notification failed:', e);
        }
      }
      return true;
    } catch (e) {
      console.error('Failed to create task from action item:', e);
      alert(e.message || 'Could not create the task. Please try again.');
      return false;
    } finally {
      setCreating((prev) => ({ ...prev, [idx]: false }));
    }
  };

  const refreshRecordingTasks = async () => {
    if (!summary) return;
    try {
      setRecordingTasks(await loadTasksForRecording(db, summary.recordingId));
    } catch (e) {
      console.error('Failed to refresh tasks for recording:', e);
    }
  };

  const handleCreateTask = async (idx) => {
    await createTaskAt(idx);
    await refreshRecordingTasks();
  };

  const handleCreateAll = async () => {
    if (!summary) return;
    const items = (summary.actionItems || []).filter((item) => actionItemText(item));
    for (let idx = 0; idx < items.length; idx++) {
      const keys = itemKeys[idx] || null;
      if (findTaskForActionItem(items[idx], keys, recordingTasks)) continue;
      // eslint-disable-next-line no-await-in-loop
      await createTaskAt(idx);
    }
    await refreshRecordingTasks();
  };

  const handleDeleteMeeting = async () => {
    if (!selectedReport?.id) return;
    if (!window.confirm('Are you sure you want to delete this meeting report? This action cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'meetingReports', selectedReport.id));
      setSelectedId(null);
      setCurrentSection(0);
    } catch (error) {
      console.error('Error deleting meeting report:', error);
      alert('Failed to delete meeting report. Please try again.');
    }
  };

  /* ---------- derived ---------- */

  const creatorName = (report) => userById[report.createdBy]?.name || '';

  const filteredMeetings = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter((r) => {
      const haystack = [
        r._date ? fmtLongDate(r._date) : '',
        r._date ? r._date.toLocaleDateString() : '',
        r._date ? MONTH_NAMES[r._date.getMonth()] : '',
        creatorName(r),
        r.fathom?.meetingTitle || '',
        r.fathom ? 'recording fathom' : 'no recording',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, searchQuery, userById]);

  useEffect(() => setCurrentPage(1), [searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredMeetings.length / MEETINGS_PER_PAGE));
  const pageStart = (Math.min(currentPage, totalPages) - 1) * MEETINGS_PER_PAGE;
  const currentMeetings = filteredMeetings.slice(pageStart, pageStart + MEETINGS_PER_PAGE);

  const withRecording = reports.filter((r) => r.fathom).length;
  const actionItemTotal = reports.reduce((n, r) => n + (r.fathom?.actionItemCount || 0), 0);
  const latest = reports[0]?._date || null;

  const sections = useMemo(() => {
    if (!selectedReport) return [];
    const v2 = isV2(selectedReport);
    return [
      { key: 'overview', title: 'Overview', icon: '🧭' },
      ...(v2 ? [{ key: 'growth', title: 'Growth — Last 30 Days', icon: '📈' }] : []),
      { key: 'clients', title: 'Clients', icon: '🏢' },
      { key: 'tasks', title: 'Outstanding Tasks', icon: '✅' },
      { key: 'additional', title: 'Additional Items', icon: '📝' },
      { key: 'recording', title: 'Recording & Action Items', icon: '🎥' },
    ];
  }, [selectedReport]);

  const section = sections[Math.min(currentSection, Math.max(0, sections.length - 1))];

  /* ---------- render ---------- */

  return (
    <div className="min-h-screen p-4 md:p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 relative">
          <button
            type="button"
            onClick={() => navigate('/crm/reports')}
            className="p-2 rounded-xl bg-white dark:bg-gray-800 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors z-10"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full text-center">
            Company Meetings
          </h1>
          <div className="w-10 h-10" />
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <CrmStatCard icon={Calendar} label="Meeting reports" value={reports.length} tone="red" compact />
          <CrmStatCard icon={Video} label="With recording" value={withRecording} tone="green" compact />
          <CrmStatCard icon={ListChecks} label="Action items" value={actionItemTotal} tone="blue" compact />
          <CrmStatCard icon={Presentation} label="Last meeting" value={latest ? fmtShortDate(latest) : '—'} tone="gray" compact />
        </div>

        {/* Search + Meeting Mode */}
        <div className={`${CARD} p-4 md:p-6 mb-6`}>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by date, month, presenter, or recording title…"
                className="w-full pl-10 pr-10 py-2 rounded-xl border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate('/crm/meeting-mode')}
              className="px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap font-medium"
            >
              <Presentation className="h-4 w-4" />
              Meeting Mode
            </button>
          </div>
        </div>

        {loading && (
          <div className={`${CARD} p-8 text-center`}>
            <BBALoader size={48} className="mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading meetings...</p>
          </div>
        )}

        {!loading && filteredMeetings.length === 0 && (
          <div className={`${CARD} p-8 text-center`}>
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No meetings found</h3>
            <p className="text-gray-600 dark:text-gray-400">
              {reports.length === 0 ? 'Run Meeting Mode and press Finish to save the first report.' : 'Try adjusting your search.'}
            </p>
          </div>
        )}

        {!loading && filteredMeetings.length > 0 && (
          <div className={`${CARD} p-4 md:p-6`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {currentMeetings.map((report) => {
                const by = creatorName(report);
                const creator = userById[report.createdBy];
                return (
                  <button
                    type="button"
                    key={report.id}
                    onClick={() => setSelectedId(report.id)}
                    className={`${TILE} p-5 text-center hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col items-center gap-2`}
                  >
                    <span className="text-3xl leading-none" aria-hidden="true">
                      📋
                    </span>
                    <div className="text-base font-semibold text-gray-900 dark:text-white leading-snug">
                      {report._date ? fmtLongDate(report._date) : 'Unknown date'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                      {report._date && <span>{fmtTime(report._date)}</span>}
                      {by && (
                        <>
                          <span>·</span>
                          <Avatar
                            src={creator?.profileImage || creator?.photoURL || creator?.profilePicture}
                            name={by}
                            size="w-5 h-5"
                          />
                          <span>{by}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center justify-center gap-1.5 flex-wrap mt-1">
                      {report.fathom ? <Pill tone="green">🎥 Recording</Pill> : <Pill>No recording yet</Pill>}
                      {report.fathom?.actionItemCount > 0 && (
                        <Pill tone="blue">✅ {report.fathom.actionItemCount} action items</Pill>
                      )}
                      {!isV2(report) && <Pill tone="amber">Legacy format</Pill>}
                    </div>
                  </button>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                  Page {Math.min(currentPage, totalPages)} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-2 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Report modal */}
      {selectedReport && section && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${CARD} w-full max-w-7xl max-h-[92vh] overflow-hidden flex flex-col`}>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-center relative">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Meeting Report — {selectedDate ? fmtLongDate(selectedDate) : 'Unknown date'}
                </h2>
                <div className="mt-1 flex items-center justify-center gap-2 flex-wrap text-xs text-gray-500 dark:text-gray-400">
                  {selectedDate && <span>Saved {fmtTime(selectedDate)}</span>}
                  {creatorName(selectedReport) && <span>· by {creatorName(selectedReport)}</span>}
                  {selectedReport.fathom ? (
                    <Pill tone="green">🎥 Recording linked</Pill>
                  ) : (
                    <Pill>No recording yet</Pill>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="absolute right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Section tabs + prev/next */}
            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCurrentSection((p) => (p > 0 ? p - 1 : sections.length - 1))}
                className="flex items-center px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div className="flex-1 flex items-center justify-center gap-2 overflow-x-auto">
                {sections.map((s, i) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setCurrentSection(i)}
                    className={`whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      i === currentSection
                        ? 'bg-red-600 border-red-600 text-white'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className="mr-1.5" aria-hidden="true">
                      {s.icon}
                    </span>
                    {s.title}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setCurrentSection((p) => (p < sections.length - 1 ? p + 1 : 0))}
                className="flex items-center px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="flex flex-col items-center mb-6">
                <span className="text-3xl leading-none" aria-hidden="true">
                  {section.icon}
                </span>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mt-2 text-center">{section.title}</h2>
                {!isV2(selectedReport) && section.key === 'overview' && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-center">
                    Saved by the previous Meeting Mode — shown in the legacy layout.
                  </p>
                )}
              </div>

              {section.key === 'overview' && <OverviewSection report={selectedReport} reportDate={selectedDate} />}
              {section.key === 'growth' && <GrowthSection growth={selectedReport.growth} />}
              {section.key === 'clients' && <ClientsSection report={selectedReport} />}
              {section.key === 'tasks' && <TasksSection report={selectedReport} reportDate={selectedDate} />}
              {section.key === 'additional' && <AdditionalSection report={selectedReport} />}
              {section.key === 'recording' && (
                <RecordingSection
                  report={selectedReport}
                  reportDate={selectedDate}
                  summary={summary}
                  summaryLoading={summaryLoading}
                  autoLinking={autoLinking}
                  internalUsers={internalUsers}
                  recordingTasks={recordingTasks}
                  itemKeys={itemKeys}
                  assigneeById={assigneeById}
                  onPickAssignee={(idx, uid) => setAssigneeById((prev) => ({ ...prev, [idx]: uid }))}
                  creating={creating}
                  onCreateTask={handleCreateTask}
                  onCreateAll={handleCreateAll}
                  onOpenPicker={() => setPickerOpen(true)}
                  onUnlink={handleUnlink}
                  onRetryAutoLink={handleRetryAutoLink}
                />
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
              <button
                type="button"
                onClick={handleDeleteMeeting}
                className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <RecordingPicker
        open={pickerOpen && !!selectedReport}
        onClose={() => setPickerOpen(false)}
        reportDate={selectedDate}
        internalUsers={internalUsers}
        onLink={handleManualLink}
        excludeSummaryId={linkedSummaryId}
      />
    </div>
  );
};

export default MeetingsReports;
